import { useId } from 'react'
import { cx } from '../../cx'

export interface SparklineProps {
  data: number[]
  width?: number
  height?: number
  /** CSS color; defaults to the accent token. */
  color?: string
  /** Fill the area under the line. */
  area?: boolean
  /** Mark the last point. */
  dot?: boolean
  className?: string
}

/**
 * Inline trend line for stat tiles and table cells. Decorative by design —
 * aria-hidden, since the adjacent value + delta carry the information.
 */
export function Sparkline({
  data,
  width = 120,
  height = 32,
  color = 'var(--bui-accent)',
  area = true,
  dot = true,
  className,
}: SparklineProps) {
  const gid = useId()
  if (data.length < 2) return null

  const min = Math.min(...data)
  const max = Math.max(...data)
  const span = max - min || 1
  const pad = 2
  const step = (width - pad * 2) / (data.length - 1)
  const y = (v: number) => pad + (height - pad * 2) * (1 - (v - min) / span)
  const pts = data.map((v, i) => [pad + i * step, y(v)] as const)
  const line = pts.map(([px, py], i) => `${i ? 'L' : 'M'}${px.toFixed(1)},${py.toFixed(1)}`).join(' ')
  const last = pts[pts.length - 1]!

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={cx('bui-sparkline', className)}
      aria-hidden="true"
      focusable="false"
    >
      {area ? (
        <>
          <defs>
            <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.28" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d={`${line} L${last[0].toFixed(1)},${height - pad} L${pad},${height - pad} Z`}
            fill={`url(#${gid})`}
          />
        </>
      ) : null}
      <path d={line} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      {dot ? <circle cx={last[0]} cy={last[1]} r="2.4" fill={color} /> : null}
    </svg>
  )
}
